import React from 'react';
import { ModerationModel } from './useModeration';
import { SEVERITY_CONFIG, TYPE_LABELS, CATEGORY_LABELS, STATUS_ACTIONS } from './moderationTypes';
import { AdminModal } from '../../pages/admin/ConfirmationDialogs';

const sevConfig = (s: string) => SEVERITY_CONFIG[s] || SEVERITY_CONFIG.low;

const ReviewModal: React.FC<{ model: ModerationModel }> = ({ model }) => {
  const {
    reviewReport, setReviewReport, reviewStatus, setReviewStatus, reviewNotes,
    setReviewNotes, reviewAction, setReviewAction, reviewing, handleReview,
    formatDateTime, inputStyle,
  } = model;

  if (!reviewReport) return null;

  return (
    <AdminModal ariaLabel="Review report" onCancel={() => setReviewReport(null)} maxWidth="32rem">
        <h3 className="text-lg font-bold mb-1" style={{ color: 'var(--rp-text)' }}>Review Report</h3>

        {/* Report summary */}
        <div className="p-3 rounded-lg mb-4" style={{ backgroundColor: 'var(--rp-surface-secondary)' }}>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: sevConfig(reviewReport.severity).bg, color: sevConfig(reviewReport.severity).color }}>
              {sevConfig(reviewReport.severity).label}
            </span>
            <span className="text-sm font-semibold" style={{ color: 'var(--rp-text)' }}>
              {TYPE_LABELS[reviewReport.report_type]}
            </span>
            <span className="text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>{CATEGORY_LABELS[reviewReport.category]}</span>
          </div>
          <div className="text-xs mb-1" style={{ color: 'var(--rp-text-secondary)' }}>
            <strong>Reported:</strong> {reviewReport.reported_display_name || reviewReport.reported_email}
            {reviewReport.total_reports_against_user > 1 && (
              <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500/20 text-red-400">
                {reviewReport.total_reports_against_user} total reports
              </span>
            )}
          </div>
          <div className="text-xs mb-2" style={{ color: 'var(--rp-text-secondary)' }}>
            <strong>By:</strong> {reviewReport.reporter_display_name || 'Unknown'} · {formatDateTime(reviewReport.created_at)}
          </div>
          <div className="text-sm p-2 rounded-lg" style={{ backgroundColor: 'var(--rp-surface)', color: 'var(--rp-text)' }}>
            {reviewReport.reason}
          </div>
          {reviewReport.event_title && (
            <div className="text-xs mt-1" style={{ color: 'var(--rp-text-tertiary)' }}>Event: {reviewReport.event_title}</div>
          )}
        </div>

        {/* Decision */}
        <div className="mb-4">
          <span className="text-xs font-semibold mb-2 block" style={{ color: 'var(--rp-text-secondary)' }} id="mod-decision-label">Decision</span>
          <div className="flex gap-2" role="group" aria-labelledby="mod-decision-label">
            {STATUS_ACTIONS.map(a => (
              <button key={a.value}
                onClick={() => setReviewStatus(a.value)}
                className="px-3 py-2 rounded-lg text-xs font-semibold transition-all flex-1"
                style={{
                  backgroundColor: reviewStatus === a.value ? `${a.color}20` : 'var(--rp-surface-secondary)',
                  color: reviewStatus === a.value ? a.color : 'var(--rp-text-secondary)',
                  border: `1px solid ${reviewStatus === a.value ? `${a.color}40` : 'var(--rp-border)'}`,
                }}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>

        {/* Action taken (for warning/action) */}
        {reviewStatus !== 'dismissed' && (
          <div className="mb-4">
            <label htmlFor="mod-review-action" className="text-xs font-semibold mb-1 block" style={{ color: 'var(--rp-text-secondary)' }}>Action Taken</label>
            <select id="mod-review-action" value={reviewAction} onChange={e => setReviewAction(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm mb-2" style={inputStyle}>
              <option value="">Select action...</option>
              <option value="verbal_warning">Verbal Warning (logged only)</option>
              <option value="content_removed">Content Removed</option>
              <option value="suspended_24h">Suspended 24 Hours</option>
              <option value="suspended_7d">Suspended 7 Days</option>
              <option value="suspended_30d">Suspended 30 Days</option>
              <option value="suspended_permanent">Permanently Suspended</option>
              <option value="account_deleted">Account Deleted (GDPR)</option>
            </select>
          </div>
        )}

        {/* Notes */}
        <div className="mb-6">
          <label htmlFor="mod-review-notes" className="text-xs font-semibold mb-1 block" style={{ color: 'var(--rp-text-secondary)' }}>Admin Notes (optional)</label>
          <textarea id="mod-review-notes" value={reviewNotes}
            onChange={e => setReviewNotes(e.target.value)}
            placeholder="Internal notes about this review..."
            className="w-full px-3 py-2 rounded-lg text-sm resize-none" rows={2} style={inputStyle}
          />
        </div>

        <div className="flex gap-3 justify-end">
          <button onClick={() => setReviewReport(null)} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--rp-text-secondary)' }}>Cancel</button>
          <button onClick={handleReview} disabled={reviewing || (reviewStatus !== 'dismissed' && !reviewAction)}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 transition-all"
            style={{ backgroundColor: STATUS_ACTIONS.find(a => a.value === reviewStatus)?.color || '#6b7280' }}
          >
            {reviewing ? 'Submitting...' : `Confirm: ${STATUS_ACTIONS.find(a => a.value === reviewStatus)?.label}`}
          </button>
        </div>
    </AdminModal>
  );
};

export default ReviewModal;
